import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PrivacyNotice from "@/components/PrivacyNotice";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(email, password, name);
      toast.success("Welcome to Folio.");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] max-w-md mx-auto px-6 py-20">
      <div className="label-eyebrow text-sage mb-3">Join Folio</div>
      <h1 className="font-serif text-5xl tracking-tight mb-2">Start your shelf.</h1>
      <p className="text-muted_ink mb-10">Free to join, books you buy are yours forever.</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="label-eyebrow block mb-2">Name</label>
          <Input
            data-testid="register-name-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12 bg-transparent border border-edge rounded-none focus-visible:ring-terracotta focus-visible:ring-1"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Email</label>
          <Input
            data-testid="register-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 bg-transparent border border-edge rounded-none focus-visible:ring-terracotta focus-visible:ring-1"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Password</label>
          <Input
            data-testid="register-password-input"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 bg-transparent border border-edge rounded-none focus-visible:ring-terracotta focus-visible:ring-1"
          />
        </div>
        {error && (
          <div data-testid="register-error" className="text-sm text-terracotta border-l-2 border-terracotta pl-3">{error}</div>
        )}
        <button
          data-testid="register-submit-btn"
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-60"
        >
          {loading ? "Creating account…" : "Create account"}
        </button>
        <PrivacyNotice
          testid="register-privacy"
          purpose="We use your email and name to create your account and secure your library."
        />
      </form>

      <div className="text-center mt-8 text-sm text-muted_ink">
        Already have an account?{" "}
        <Link to="/login" data-testid="link-to-login" className="text-ink underline underline-offset-4 decoration-terracotta">
          Sign in
        </Link>
      </div>
    </div>
  );
}
